import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Clock, BookOpen, Filter, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Prévention & Droits', 'Santé & Bien-être', 'Famille & Éducation',
  'Compétences pro', 'Culture & Société', 'Entreprise'];
const TYPE_LABELS = { editorial: '📰 Éditorial', invite: '👤 Invité', etude: '📊 Étude & Chiffres', prolongement: '🔗 Cours lié' };

function ArticleCard({ article, featured = false }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/articles/${article.slug}`)}
      data-testid={`article-card-${article.article_id}`}
      className={`group text-left bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/20 rounded-2xl overflow-hidden transition-all duration-200 ${featured ? 'md:flex' : ''}`}
    >
      {article.cover_image && (
        <div className={`overflow-hidden ${featured ? 'md:w-2/5 h-48 md:h-auto' : 'h-40'}`}>
          <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
      )}
      <div className={`p-4 ${featured ? 'md:flex-1' : ''}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs bg-[#ff6b6b]/15 text-[#ff6b6b] px-2 py-0.5 rounded-full">{article.category}</span>
          {article.article_type && <span className="text-xs text-[#a0a0ab]">{TYPE_LABELS[article.article_type]}</span>}
        </div>
        <h3 className={`font-semibold text-white group-hover:text-[#ff6b6b] transition-colors line-clamp-2 ${featured ? 'text-lg mb-2' : 'text-sm mb-2'}`}>
          {article.title}
        </h3>
        {article.meta_description && (
          <p className="text-xs text-[#a0a0ab] line-clamp-2 mb-3">{article.meta_description}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-[#a0a0ab]">
          <span>{article.author_name}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock size={10} /> {article.estimated_reading_time} min</span>
        </div>
      </div>
    </button>
  );
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(search);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (category) params.category = category;
      if (search) params.search = search;
      const [listRes, featuredRes] = await Promise.all([
        axios.get(`${API}/api/articles`, { params }),
        !page || page === 1 ? axios.get(`${API}/api/articles/featured`) : Promise.resolve({ data: { article: null } })
      ]);
      setArticles(listRes.data.articles);
      setTotal(listRes.data.total);
      setPages(listRes.data.pages);
      if (featuredRes.data.article) setFeatured(featuredRes.data.article);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchArticles(); }, [category, search, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(p => { const n = new URLSearchParams(p); n.set('search', searchInput); n.set('page', '1'); return n; });
  };

  return (
    <>
      <Helmet>
        <title>Articles & Ressources — Kurso</title>
        <meta name="description" content="Découvrez nos articles sur la prévention, la santé, l'éducation et les compétences professionnelles." />
      </Helmet>
      <div className="min-h-screen bg-[#0f0e17]">
        <header className="sticky top-0 z-50 bg-[#0f0e17]/90 backdrop-blur-xl border-b border-white/5 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <a href="/dashboard" className="font-heading font-bold text-xl text-white">Kurso</a>
            <a href="/articles" className="text-sm text-[#ff6b6b] font-medium">Articles</a>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-white mb-2">Articles & Ressources</h1>
            <p className="text-[#a0a0ab]">Explorez nos contenus éditoriaux sur des sujets qui comptent</p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="relative mb-6">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a0a0ab]" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Rechercher un article..."
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-2xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
              data-testid="articles-search"
            />
          </form>

          {/* Category filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
            <button
              onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.delete('category'); n.set('page', '1'); return n; })}
              className={`text-xs px-3 py-1.5 rounded-full flex-shrink-0 transition-colors border ${!category ? 'bg-[#ff6b6b] text-white border-[#ff6b6b]' : 'bg-white/5 text-[#e8e8e8] border-white/10 hover:bg-white/10'}`}
              data-testid="category-all"
            >
              Tous
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('category', cat); n.set('page', '1'); return n; })}
                className={`text-xs px-3 py-1.5 rounded-full flex-shrink-0 transition-colors border whitespace-nowrap ${category === cat ? 'bg-[#ff6b6b] text-white border-[#ff6b6b]' : 'bg-white/5 text-[#e8e8e8] border-white/10 hover:bg-white/10'}`}
                data-testid={`category-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Featured article */}
          {featured && page === 1 && !category && !search && (
            <div className="mb-8">
              <p className="text-xs text-[#ffd93d] font-medium mb-3 flex items-center gap-1.5">
                <Star size={12} fill="#ffd93d" /> Article à la une
              </p>
              <ArticleCard article={featured} featured />
            </div>
          )}

          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white/5 rounded-2xl h-64 animate-pulse" />
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16 text-[#a0a0ab]">
              <BookOpen size={40} className="mx-auto mb-4 opacity-40" />
              <p>Aucun article trouvé</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map(a => <ArticleCard key={a.article_id} article={a} />)}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button disabled={page === 1} onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('page', String(page - 1)); return n; })}
                className="p-2 rounded-lg bg-white/5 disabled:opacity-40 hover:bg-white/10 transition-colors">
                <ChevronLeft size={16} className="text-white" />
              </button>
              <span className="text-sm text-[#e8e8e8]">Page {page} / {pages}</span>
              <button disabled={page === pages} onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('page', String(page + 1)); return n; })}
                className="p-2 rounded-lg bg-white/5 disabled:opacity-40 hover:bg-white/10 transition-colors">
                <ChevronRight size={16} className="text-white" />
              </button>
            </div>
          )}

          <Footer />
        </main>
      </div>
    </>
  );
}
